/**
 * The candidate styles, and the legibility kit each one needs.
 *
 * A skin is TWO things, and conflating them is how a globe ends up beautiful
 * and unplayable:
 *
 *   1. IMAGERY — a local raster tile pyramid from `packages/data/assets/earth`,
 *      plus its runtime grade (`raster-*` paint properties).
 *   2. A LEGIBILITY KIT — the border, highlight, microstate-dot and pin
 *      treatment that keeps the pinpoint answerable ON that imagery.
 *
 * The kit is a toggle in the lab precisely so the owner can turn it off and see
 * the naive result: imagery in, borders gone. That comparison is the argument
 * for every line of `labStyle.ts`.
 *
 * THE ONE IDEA WORTH TAKING AWAY. A single border colour cannot survive
 * satellite imagery, because the imagery spans the whole luminance range: a
 * white line dies on Greenland's ice (measured 186) and a dark line dies on the
 * ocean (measured 25). Every kit here draws the border TWICE — a wide casing
 * and a narrow line, at OPPOSITE ends of the luminance range. Whatever the
 * terrain does, one of the two is in contrast with it. That is why `casing` is
 * light for `atlas` (bright land) and near-black for the dark skins, and it is
 * the whole reason borders stay readable over desert, ice, forest and sea.
 *
 * Luminance figures quoted below are the bake lane's, measured off the decoded
 * WebP: see `packages/data/assets/earth/README.md`.
 */

import { COLORS, SNAP } from './theme';

export type SkinId =
  | 'baseline'
  | 'slate-relief'
  | 'marble'
  | 'atlas'
  | 'nightfall'
  | 'terra';

/** Which way round the border pair, dots and graticule are painted. */
export type Palette = 'dark-land' | 'bright-land';

export interface RasterGrade {
  /** `raster-opacity` at and below the home view. */
  readonly opacityHome: number;
  /**
   * `raster-opacity` from map zoom 5 up.
   *
   * Ramping the imagery OUT was the original intent: zoom 5+ is the
   * disambiguate/magnify window, where the player is doing precision work and
   * arguably wants the flat maximally legible look back — and it is also where
   * the level-4 tiles are being overzoomed and start to look soft.
   *
   * `terra` sets this to 1 and declines the whole argument. Zooming in to
   * inspect a coastline and watching the planet drain to grey slate is a worse
   * experience than a slightly soft photograph, the border pair was already
   * measured against imagery at FULL strength (that is what the home view is),
   * and the vectors that carry precision — borders, dots, pins, the answer ring
   * — are drawn on top and stay crisp at any magnification. Softness costs
   * fidelity the player was not using; greyness costs the globe.
   */
  readonly opacityDeep: number;
  /** -1..1. Negative desaturates. */
  readonly saturation: number;
  /** -1..1. */
  readonly contrast: number;
  /** 0..1, floor of the output range. Lifts shadow terrain off pure black. */
  readonly brightnessMin: number;
  /** 0..1, ceiling. Below 1 this is the knob that stops the Sahara blinding. */
  readonly brightnessMax: number;
}

export interface LegibilityKit {
  readonly palette: Palette;
  /** Narrow, on top. */
  readonly borderLine: string;
  /** Wide, underneath, at the opposite end of the luminance range. */
  readonly borderCasing: string;
  readonly borderCasingOpacity: number;
  /** Multiplier on the shipped `line-width` ramp. */
  readonly borderWidthMult: number;
  /** Extra width, in px, the casing gets over the line. */
  readonly casingWidthPx: number;
  readonly graticule: string;
  readonly graticuleOpacity: number;
  /** Translucent overlay opacity for a correct/incorrect country. */
  readonly highlightOpacity: number;
  /** A flat wash of `COLORS.land` over land only. 0 = pure imagery. */
  readonly landVeilOpacity: number;
  /**
   * The same wash at and above the zoom where the imagery has finished fading
   * out. This is the one that stops deep zoom going dark — and it is dead
   * weight for a skin like `terra` whose imagery never fades, which sets it
   * to 0 so nothing is washed over a photograph that is still fully there.
   *
   * The imagery ramps to `grade.opacityDeep` above zoom 2.5 on purpose — that
   * is the magnify/disambiguate window, where the player wants the flat,
   * maximally legible globe back. But the land FILL is held at
   * `landVeilOpacity` (0 for most skins), so as the photograph faded there was
   * nothing underneath painting land: continents and islands drained to the
   * ocean's own background colour and the globe became outlines on a dark
   * field. Ramping the veil up as the imagery ramps down is what actually
   * returns the flat globe, instead of merely removing the imagery.
   */
  readonly landVeilDeep: number;
  /**
   * Fill of the microstate dot itself.
   *
   * Explicit per kit rather than derived from `palette`, because the two are no
   * longer the same question. The dot stands for a COUNTRY WITH NO POLYGON —
   * Nauru, Tuvalu, Kiribati, the Marshall Islands — and those countries have no
   * landmass in the imagery either, so this dot is the only thing on the globe
   * that says "there is land here". It should therefore be the colour of land,
   * not a neutral marker colour: on a skin with a real blue sea a slate-grey
   * disc with a white ring reads as a pin dropped on open water.
   */
  readonly microFill: string;
  /**
   * Dot radius at zoom 0, in CSS pixels, overriding `SNAP.microDotRadiusPx`.
   *
   * Visual only. Nothing picks the dot by its circle — `snap.ts` projects the
   * position and measures screen distance — so this changes how big a
   * geometry-less country LOOKS and not one thing about how it is answered.
   */
  readonly microRadiusPx: number;
  /** Disc painted UNDER every microstate dot so it cannot sink into terrain. */
  readonly microUnder: string;
  readonly microUnderOpacity: number;
  readonly microUnderBoostPx: number;
  readonly microEdge: string;
  readonly microEdgeWidth: number;
  /** Ring drawn round every pin so it survives a busy background. */
  readonly pinStroke: string;
}

export interface Skin {
  readonly id: SkinId;
  readonly title: string;
  /** One line for the switcher. */
  readonly blurb: string;
  /** Longer note shown in the panel. Honest, including the costs. */
  readonly note: string;
  /** Relative, same-origin. Null for the vector baseline. */
  readonly tiles: string | null;
  readonly attribution: string;
  /** Committed bytes for the whole z0-z4 pyramid. */
  readonly pyramidBytes: number;
  /** Bake lane's measured worst-case level-2 first paint. */
  readonly firstPaintBytes: number;
  readonly landLuminance: readonly [number, number];
  /** Brightest sampled open-ocean landmark. The land floor has to clear it. */
  readonly oceanLuminance: number;
  /**
   * Opacity of the animated ocean swell drawn UNDER the imagery. 0 for every
   * skin whose bake painted an opaque sea — there the swell would be invisible,
   * so drawing it would be pure cost. See `waves.ts`.
   */
  readonly swell: number;
  /**
   * Colour of the background the imagery is composited over, when the skin
   * wants something other than `COLORS.ocean`.
   *
   * Only a translucent-sea skin needs this. Its ocean pixels land at 75%
   * opacity, so a quarter of every sea pixel on screen is whatever is behind
   * them; leaving that as the flat slate `#0d1b2a` would wash a quarter of the
   * grey back into a bathymetry that was graded to be blue. Matching the base
   * to the ramp's own deep end means the composite reads as the texture, and
   * the swell modulates it rather than fighting it.
   */
  readonly oceanBase: string | null;
  readonly grade: RasterGrade;
  readonly kit: LegibilityKit;
}

/**
 * Dark-land kit. Land sits between luminance 34 and 186 depending on the skin
 * and the terrain, ocean is the game's flat `#0d1b2a` (25). A near-white line
 * over a near-black casing keeps contrast at both ends.
 */
const DARK_KIT: LegibilityKit = {
  palette: 'dark-land',
  borderLine: '#e8f1fb',
  borderCasing: '#04070c',
  borderCasingOpacity: 0.85,
  borderWidthMult: 1.35,
  casingWidthPx: 1.8,
  graticule: '#1d3047',
  graticuleOpacity: 0.35,
  highlightOpacity: 0.62,
  landVeilOpacity: 0,
  landVeilDeep: 0.88,
  microFill: COLORS.microstate,
  microRadiusPx: SNAP.microDotRadiusPx,
  microUnder: '#03060b',
  microUnderOpacity: 0.72,
  microUnderBoostPx: 2.4,
  microEdge: '#eaf2fb',
  microEdgeWidth: 1.1,
  pinStroke: '#04070c',
};

/**
 * Bright-land kit. `atlas` land measures 156-216 while its ocean is still the
 * game's 25, so the pair inverts: a dark line carries the land, a light casing
 * carries the coast and the sea. This is the "second inverted palette" the bake
 * lane flagged as the real cost of adopting Atlas, written out.
 */
const BRIGHT_KIT: LegibilityKit = {
  palette: 'bright-land',
  borderLine: '#0b1622',
  borderCasing: '#f2f7fd',
  borderCasingOpacity: 0.8,
  borderWidthMult: 1.3,
  casingWidthPx: 2.2,
  graticule: '#4a627c',
  graticuleOpacity: 0.28,
  highlightOpacity: 0.58,
  landVeilOpacity: 0,
  landVeilDeep: 0.88,
  microFill: '#16293c',
  microRadiusPx: SNAP.microDotRadiusPx,
  microUnder: '#f4f9ff',
  microUnderOpacity: 0.85,
  microUnderBoostPx: 2.6,
  microEdge: '#0b1622',
  microEdgeWidth: 1.2,
  pinStroke: '#061019',
};

const tileUrl = (id: string): string => `/earth/tiles/${id}/{z}/{x}/{y}.webp`;

export const SKINS: readonly Skin[] = [
  {
    id: 'terra',
    title: 'Terra',
    blurb: 'Blue Marble with the mountains and the sea put back. 2.50 MiB.',
    note:
      'The shipped globe. Blue Marble colour with Natural Earth 50m shaded relief multiplied ' +
      'in before the luminance remap, so the Andes, the Himalaya and the Atlas read as terrain ' +
      'rather than as ground cover; and the source bathymetry kept rather than flattened, so ' +
      'the other 70% of the sphere has ridges, trenches and continental shelves in it. Sea ' +
      'pixels are baked at 75% alpha, which is what the animated swell shows through — see ' +
      'waves.ts. The cost is honest: land/ocean luminance separation drops from Marble’s 52 ' +
      'to 28, and what replaces it is chroma, a saturated blue sea against ' +
      'desaturated land. The border pair carries the coastline either way.',
    tiles: tileUrl('terra'),
    attribution:
      'Earth imagery: NASA Earth Observatory (Blue Marble: Next Generation), Reto Stockli. ' +
      'Shaded relief: Natural Earth (SR_50M, public domain).',
    pyramidBytes: 2_618_672,
    firstPaintBytes: 201_832,
    landLuminance: [78, 179],
    oceanLuminance: 50,
    swell: 0.8,
    /** The deep end of the bake's own ocean ramp, `SEA.deep` in fetchearth.ts. */
    oceanBase: '#08182e',
    grade: {
      opacityHome: 1,
      // Full strength at every zoom. See `RasterGrade.opacityDeep` for why this
      // skin declines the ramp-out the others take.
      opacityDeep: 1,
      // The bake already graded this skin end to end. These are trims, not a
      // second pass: the relief multiply is doing the contrast work, so adding
      // more here only crushes the shadow side of every range.
      saturation: 0,
      contrast: 0,
      brightnessMin: 0.02,
      brightnessMax: 0.97,
    },
    /*
     * The dot kit is retuned for a sea that is now actually blue.
     *
     * `microFill` is an island green rather than the shipped slate: it is the
     * same tone the bake's island rescue paints Vanuatu and the Maldives with,
     * so a geometry-less country reads as the same KIND of thing as its
     * neighbours that do have polygons. It is also the more legible of the two
     * and not by a little — measured against this skin's open ocean (luminance
     * 32) the green sits at 91 for a step of 59, where the old slate managed 43.
     *
     * `microUnder` stops being near-black for the same reason. A black halo on
     * flat slate reads as a shadow; on deep water it reads as a hole. A deeper
     * blue than the sea keeps the luminance step at the dot's edge and lets the
     * halo pass for the shoal around an island.
     */
    kit: {
      ...DARK_KIT,
      landVeilOpacity: 0,
      // Nothing to compensate for: the imagery is at full strength at every
      // zoom, so a wash of flat slate over it would only mute the colour this
      // skin exists to show.
      landVeilDeep: 0,
      microFill: '#7d8a5e',
      // A shade wider than the 2.6px the flat globe uses. These countries have
      // no polygon and, at 6.7 km per source texel, no landmass in the imagery
      // either — the dot is the entire depiction, and at 2.6px on a 400px globe
      // it was reading as a speck of dust rather than as an island.
      microRadiusPx: 3.2,
      microUnder: '#04101f',
      microUnderOpacity: 0.82,
      microUnderBoostPx: 2.8,
      microEdge: '#e9efdc',
      microEdgeWidth: 1,
    },
  },
  {
    id: 'baseline',
    title: 'Baseline (shipped)',
    blurb: 'Flat dark-slate vectors. Today’s globe, unmodified.',
    note:
      'Exactly what buildStyle() from @geogame/globe returns: no kit, no imagery, not one ' +
      'property changed. This is the control, and every number the other skins post is only ' +
      'interesting relative to this one.',
    tiles: null,
    attribution: 'Country polygons: Natural Earth 110m (public domain).',
    pyramidBytes: 0,
    firstPaintBytes: 0,
    landLuminance: [56, 56],
    oceanLuminance: 25,
    swell: 0,
    oceanBase: null,
    grade: {
      opacityHome: 0,
      opacityDeep: 0,
      saturation: 0,
      contrast: 0,
      brightnessMin: 0,
      brightnessMax: 1,
    },
    kit: { ...DARK_KIT, borderLine: '#54708f', borderCasingOpacity: 0, borderWidthMult: 1 },
  },
  {
    id: 'slate-relief',
    title: 'Slate Relief',
    blurb: 'Shaded relief in the shipped palette. 0.52 MiB.',
    note:
      'Natural Earth SR_50M, graded to land 55-56 over ocean 25 — the SAME separation the flat ' +
      '#28394d/#0d1b2a globe has today. Every border, pin and dot colour keeps the contrast it ' +
      'was tuned against, so the kit is barely doing any work here. Terrain, not photography: ' +
      'you get mountain and desert relief, no colour.',
    tiles: tileUrl('slate-relief'),
    attribution: 'Made with Natural Earth (SR_50M, public domain).',
    pyramidBytes: 540_382,
    firstPaintBytes: 23_654,
    landLuminance: [55, 56],
    oceanLuminance: 25,
    swell: 0,
    oceanBase: null,
    grade: {
      opacityHome: 1,
      opacityDeep: 0.3,
      saturation: 0,
      contrast: 0.05,
      brightnessMin: 0,
      brightnessMax: 1,
    },
    kit: { ...DARK_KIT, borderLine: '#cfe0f2', borderWidthMult: 1.15, borderCasingOpacity: 0.7 },
  },
  {
    id: 'marble',
    title: 'Marble',
    blurb: 'NASA Blue Marble. The reference look, minus the labels. 1.16 MiB.',
    note:
      'What the owner actually asked for. Land runs 77-185 against ocean 25, so the imagery does ' +
      'the land/ocean job on its own — but the border now has to survive Saharan sand at one end ' +
      'and the Greenland ice sheet at the other, which is what the casing is for. The residual ' +
      'cost is real and it is over ice: a white line on 186-luminance ice is 1.55:1, and there ' +
      'the dark casing carries the contrast alone.',
    tiles: tileUrl('marble'),
    attribution:
      'Earth imagery: NASA Earth Observatory (Blue Marble: Next Generation), Reto Stockli.',
    pyramidBytes: 1_217_662,
    firstPaintBytes: 74_650,
    landLuminance: [77, 185],
    oceanLuminance: 25,
    swell: 0,
    oceanBase: null,
    grade: {
      opacityHome: 1,
      opacityDeep: 0.25,
      // The bake already graded this skin — land desaturated, luminance
      // compressed into [50, 186]. These are trims on top of that, not a second
      // full grade: over-correcting here is how Blue Marble ends up looking like
      // wet slate, which is a worse globe than either end of the choice.
      saturation: -0.05,
      contrast: 0,
      brightnessMin: 0.02,
      brightnessMax: 0.96,
    },
    kit: { ...DARK_KIT, landVeilOpacity: 0.05 },
  },
  {
    id: 'atlas',
    title: 'Atlas',
    blurb: 'Natural Earth II. Prettiest, and a bright-land palette. 2.06 MiB.',
    note:
      'The best-looking of the four and the most expensive to adopt: land measures 156-216 while ' +
      'the ocean is still the game’s 25, so it is a BRIGHT-LAND globe inside a dark product. ' +
      'It needs the inverted border pair, an inverted microstate dot, and a re-check of every HUD ' +
      'colour that overlaps the sphere. Not a texture swap — a second palette.',
    tiles: tileUrl('atlas'),
    attribution: 'Made with Natural Earth (NE2_50M_SR_W, public domain).',
    pyramidBytes: 2_154_908,
    firstPaintBytes: 102_810,
    landLuminance: [156, 216],
    oceanLuminance: 25,
    swell: 0,
    oceanBase: null,
    grade: {
      opacityHome: 1,
      opacityDeep: 0.25,
      saturation: -0.1,
      contrast: 0,
      brightnessMin: 0.04,
      brightnessMax: 0.88,
    },
    kit: BRIGHT_KIT,
  },
  {
    id: 'nightfall',
    title: 'Nightfall',
    blurb: 'NASA Black Marble city lights. Cosmetic skin, not a default. 1.13 MiB.',
    note:
      'Gorgeous and structurally unfit as a default. Over the Sahara, the Amazon, central ' +
      'Australia, Mongolia, the DRC and Greenland there are no lights, so there is no imagery for ' +
      'a coastline to sit on and the baked land floor makes those countries silhouettes. Perfect ' +
      'paid skin — with a warning on the tin.',
    tiles: tileUrl('nightfall'),
    attribution:
      'NASA Earth Observatory images by Joshua Stevens, using Suomi NPP VIIRS data (Black Marble 2016).',
    pyramidBytes: 1_184_772,
    firstPaintBytes: 100_557,
    landLuminance: [34, 79],
    oceanLuminance: 9,
    swell: 0,
    oceanBase: null,
    grade: {
      opacityHome: 1,
      opacityDeep: 0.3,
      saturation: 0.1,
      contrast: 0.1,
      brightnessMin: 0,
      brightnessMax: 1,
    },
    kit: { ...DARK_KIT, borderLine: '#cfe0f2', graticuleOpacity: 0.22 },
  },
];

export function skinById(id: SkinId): Skin {
  return SKINS.find((s) => s.id === id) ?? (SKINS[0] as Skin);
}

/**
 * Live tuning knobs. Seeded from the skin's own grade and kit; the panel writes
 * to these, `labStyle.ts` reads them, and nothing else in the lab is stateful
 * about appearance.
 */
export interface Tuning {
  kitEnabled: boolean;
  graticule: boolean;
  swellOpacity: number;
  opacityHome: number;
  opacityDeep: number;
  saturation: number;
  contrast: number;
  brightnessMin: number;
  brightnessMax: number;
  borderWidthMult: number;
  highlightOpacity: number;
  landVeilOpacity: number;
  graticuleOpacity: number;
}

export function tuningFor(skin: Skin): Tuning {
  return {
    kitEnabled: skin.id !== 'baseline',
    graticule: true,
    swellOpacity: skin.swell,
    opacityHome: skin.grade.opacityHome,
    opacityDeep: skin.grade.opacityDeep,
    saturation: skin.grade.saturation,
    contrast: skin.grade.contrast,
    brightnessMin: skin.grade.brightnessMin,
    brightnessMax: skin.grade.brightnessMax,
    borderWidthMult: skin.kit.borderWidthMult,
    highlightOpacity: skin.kit.highlightOpacity,
    landVeilOpacity: skin.kit.landVeilOpacity,
    graticuleOpacity: skin.kit.graticuleOpacity,
  };
}
